import { useCallback } from "react";

import type { BlisRole } from "@blis/config";

import { useAuth } from "@/features/auth/hooks";
import { useTinyKeys } from "@/lib/useTinyKeys";
import { useAccessionCommands } from "./AccessionCommands";
import { useAccession } from "./AccessionProvider";

export function AccessionShortcuts() {
  const { accession } = useAccession();
  const actions = useAccessionCommands();
  const { user } = useAuth();
  const keyWrapper = useCallback(
    (cb: (evt: KeyboardEvent) => void, roles: BlisRole[] = []) => {
      return (evt: KeyboardEvent) => {
        if (!user) {
          return;
        }
        if (roles.length > 0 && !roles.includes(user.profile.default_role)) {
          return;
        }
        evt.preventDefault();
        // evt.stopPropagation();
        cb(evt);
      };
    },
    [user],
  );

  useTinyKeys(
    {
      d: keyWrapper(
        () => actions.editAccession(),
        ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"],
      ),
      e: keyWrapper(
        () => actions.enterResults(),
        ["blis_lab_tech", "blis_lab_manager"],
      ),
      h: keyWrapper(
        () => actions.hold(),
        ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"],
      ),

      p: keyWrapper(
        () => actions.generateReport(),
        ["blis_lab_tech", "blis_lab_manager"],
      ),
      r: keyWrapper(() => {
        if (accession.status === "new") {
          actions.receive();
        } else if (
          accession.status !== "final" &&
          accession.status !== "in_progress" &&
          accession.status !== "completed" &&
          accession.status !== "cancelled"
        ) {
          actions.unreceive();
        }
      }, ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"]),
      u: keyWrapper(() => {
        if (accession.status === "received") {
          actions.createTestRun();
        }
      }, ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"]),

      "Shift+u": keyWrapper(() => {
        actions.upload();
      }, ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"]),

      "c c": keyWrapper(() => actions.copyCaseId()),
      "c i": keyWrapper(() => actions.copyAccessionId()),

      "l l": keyWrapper(
        () => actions.printLabels(),
        ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"],
      ),
      "l a": keyWrapper(
        () => actions.printArchiveLabels(),
        ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"],
      ),

      "s p": keyWrapper(
        () => actions.openSetPriorityCommand(),
        ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"],
      ),
      "s s": keyWrapper(
        () => actions.openSetStatusCommand(),
        ["blis_lab_assistant", "blis_lab_tech", "blis_lab_manager"],
      ),
    },
    [actions, accession, user],
  );
  return null;
}
